import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import { ToggleSwitch } from '@/components/legacy-ui/ToggleSwitch';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { WizardStepper } from '@/components/wizards';
import { useScanCadence } from '@/hooks/useScanCadence';
import { queryClient } from '@/queryclient';
import { CadenceStep } from '@/sections/settings/components/forms/CadenceStep';
import {
  ScanSchedule,
  type ScanScheduleHandle,
  type ScanScheduleValue,
} from '@/sections/settings/components/forms/ScanSchedule';
import {
  buildPending,
  buildSentence,
} from '@/sections/settings/components/forms/scanScheduleSentence';
import { SentenceText } from '@/sections/settings/components/forms/SentenceText';
import type { CadenceFrequency, ScanCadenceBase, WeekDay } from '@/types';

export interface ScanScheduleWizardProps {
  open: boolean;
  onClose: () => void;
  email?: string;
  /** Initial cadence frequency (from the current `scan_cadence_base` setting). */
  initialFrequency: CadenceFrequency;
  /** Initial manual-bypass value from the current `schedule` setting. */
  initialManualBypass: boolean;
}

/**
 * Derives the `scan_cadence_base` payload from Step-1 frequency + the UTC
 * window Step 2 wrote (FIDELITY G2/G3, §8). `dayOfWeek` = earliest selected
 * (UTC) weekday; `dayOfMonth` always 1 (UI has no day-of-month picker; backend
 * clamps 0 => 1); `time` = UTC start "HH:MM". `anchorDate` is NEVER set.
 */
function toCadencePayload(
  frequency: CadenceFrequency,
  utcDays: WeekDay[],
  utcStartHHMM: string
): ScanCadenceBase {
  const earliestDow = utcDays.length
    ? [...utcDays].sort((a, b) => a - b)[0]
    : 0;
  return {
    frequency,
    dayOfWeek: earliestDow,
    dayOfMonth: 1,
    time: utcStartHHMM,
  };
}

export const ScanScheduleWizard: React.FC<ScanScheduleWizardProps> = ({
  open,
  onClose,
  email,
  initialFrequency,
  initialManualBypass,
}) => {
  const [step, setStep] = useState<number>(1);
  const [frequency, setFrequency] =
    useState<CadenceFrequency>(initialFrequency);
  const [step2, setStep2] = useState<ScanScheduleValue | undefined>(undefined);
  const [step2Valid, setStep2Valid] = useState(false);
  const [manualBypass, setManualBypass] =
    useState<boolean>(initialManualBypass);
  const [saving, setSaving] = useState(false);

  // The manual-scan step exists only for own-tenant edits (mirrors the
  // ScanSchedule `!customerEmail` gate). Customers editing their own tenant get
  // 3 steps; Praetorian operators editing a customer (email set) get 2.
  const showManualStep = !email;
  const totalSteps = showManualStep ? 3 : 2;
  const isLastStep = step === totalSteps;

  const step2Ref = useRef<ScanScheduleHandle>(null);
  const { updateScanCadenceAsync } = useScanCadence({ email });

  // Live summary band (C4): pending sentence (E5) until ≥1 day chosen.
  const hasDays = (step2?.days?.length ?? 0) > 0;
  const pending = buildPending(frequency);
  const sentence =
    hasDays && step2
      ? buildSentence({
          frequency,
          days: step2.days,
          start: step2.start ?? '',
          end: step2.end ?? '',
        })
      : null;

  async function handleSaveSchedule() {
    if (saving) return;

    // The form is the single tz-conversion source of truth. computeUtc() derives
    // the UTC window WITHOUT writing, so we can write cadence FIRST (OPEN-Q-3
    // order) using the exact same values the schedule write will use.
    const utc = step2Ref.current?.computeUtc();
    if (!utc) {
      toast.error('Choose at least one day and a valid time window.');
      return;
    }

    setSaving(true);
    try {
      // 1) cadence (silent). If this fails, STOP — schedule is untouched, so the
      // tenant's current schedule stays consistent (least-surprise).
      try {
        await updateScanCadenceAsync(
          toCadencePayload(frequency, utc.days, utc.start)
        );
      } catch {
        toast.error('Could not save cadence. Your schedule was not changed.');
        return;
      }

      // 2) schedule (the form's own silent write of the SAME UTC window).
      const written = await step2Ref.current?.submit();
      if (!written) {
        // cadence saved, schedule failed → surface the partial state and keep
        // the wizard OPEN so the user can retry the window without losing Step 1.
        toast.error(
          'Cadence saved, but the day/time window failed to save. Reopen to retry the window.'
        );
        return;
      }

      // belt-and-suspenders: both writes invalidate ['my','setting',email]; one
      // explicit invalidation guarantees the card re-reads both (G5).
      await queryClient.invalidateQueries({ queryKey: ['my', 'setting'] });
      toast.success('Scan schedule updated');
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="bg-chariot-layer0 max-w-[720px] gap-0 overflow-hidden p-0"
        data-testid="scan-schedule-wizard"
        onOpenAutoFocus={e => {
          // C7: focus the first interactive control of step 1 (the radiogroup).
          e.preventDefault();
          const root = e.currentTarget as HTMLElement;
          root
            .querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')
            ?.focus();
        }}
      >
        {/* C2 — header: title, subtitle, brand-tertiary calendar tile */}
        <div className="border-chariot-stroke flex items-start gap-3 border-b p-4">
          <div className="bg-chariot-brand-tertiary text-chariot-brand-primary flex size-8 shrink-0 items-center justify-center rounded-md">
            <CalendarIcon className="size-4" />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-base font-bold">
              Edit Scan Schedule
            </DialogTitle>
            <DialogDescription className="text-chariot-text-secondary mt-0.5 text-xs">
              Applied to all assets in this tenant.
            </DialogDescription>
          </div>
        </div>

        {/* C3 — step indicator (canonical WizardStepper, dots variant) */}
        <div className="border-chariot-stroke bg-chariot-layer1 border-b px-4 py-3">
          <WizardStepper
            variant="dots"
            currentStep={step}
            steps={
              showManualStep
                ? [
                    { id: 'cadence', title: 'Cadence' },
                    { id: 'days-times', title: 'Days & Times' },
                    { id: 'manual', title: 'Manual scans' },
                  ]
                : [
                    { id: 'cadence', title: 'Cadence' },
                    { id: 'days-times', title: 'Days & Times' },
                  ]
            }
            ariaLabel="Scan schedule steps"
          />
        </div>

        {/* C4 — persistent live summary band */}
        <div className="border-chariot-stroke bg-chariot-brand-tertiary border-b px-4 py-3">
          <div className="text-chariot-text-secondary mb-1 text-xs font-bold tracking-wide uppercase">
            This builds the schedule
          </div>
          <p
            className="text-chariot-text-primary text-sm"
            data-testid="scan-schedule-summary-band"
          >
            {sentence ? (
              <SentenceText parts={sentence} />
            ) : (
              <>
                <SentenceText parts={pending.lead} />
                {pending.pending && (
                  <span className="text-chariot-text-disabled font-normal italic">
                    {pending.pending}
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Step bodies — fixed height + internal scroll so every step is the
            same size (no dialog resize between steps). */}
        <div className="h-[400px] min-w-0 overflow-y-auto p-5">
          {step === 1 && (
            <CadenceStep value={frequency} onChange={setFrequency} />
          )}
          {/* ScanSchedule must stay MOUNTED across Step 2 ⇄ Step 3 so its
              imperative handle (step2Ref) and day/time state survive — the Save
              button lives on Step 3 and calls step2Ref.current.submit(). Hide it
              visually when off Step 2 rather than unmounting. */}
          {step >= 2 && (
            <div
              role="group"
              aria-label="Step 2: Days and Times"
              data-testid="scan-schedule-wizard-step-2"
              className={step === 2 ? undefined : 'hidden'}
            >
              <ScanSchedule
                ref={step2Ref}
                customerEmail={email}
                embedded
                frequency={frequency}
                manualBypass={manualBypass}
                onChange={setStep2}
                onValidityChange={setStep2Valid}
              />
            </div>
          )}
          {step === 3 && (
            <div
              role="group"
              aria-label="Step 3: Manual scans"
              data-testid="scan-schedule-wizard-step-3"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <h2 className="text-md font-bold">
                    Manual scans bypass the schedule
                  </h2>
                  <p className="text-chariot-text-secondary max-w-[46ch] text-xs leading-relaxed">
                    When enabled, manually triggered scans run immediately,
                    ignoring both the cadence period and the time window.
                  </p>
                </div>
                <ToggleSwitch
                  checked={manualBypass}
                  onChange={setManualBypass}
                  ariaLabel="Toggle manual scan bypass"
                  data-testid="scan-schedule-manual-bypass-toggle"
                />
              </div>
            </div>
          )}
        </div>

        {/* C5 — footer chrome (single unified footer) */}
        <div className="border-chariot-stroke bg-chariot-layer0 flex items-center gap-3 border-t p-4">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="scan-schedule-wizard-cancel"
          >
            Cancel
          </Button>
          <div className="flex-1" />
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              data-testid="scan-schedule-wizard-back"
            >
              <ChevronLeftIcon className="size-3.5" />
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button
              variant="default"
              onClick={() => setStep(s => Math.min(totalSteps, s + 1))}
              disabled={step === 2 && !step2Valid}
              data-testid="scan-schedule-wizard-next"
            >
              Next
              <ChevronRightIcon className="size-3.5" />
            </Button>
          )}
          {isLastStep && (
            <Button
              variant="default"
              onClick={handleSaveSchedule}
              disabled={!step2Valid || saving}
              data-testid="scan-schedule-wizard-save"
            >
              Save schedule
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
